// src/pages/tenant/TenantBrandingPage.tsx
// White-label customization: logo, colors, domain, support links, custom CSS.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CssEntry {
  key: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantBrandingPage - white-label customization for the tenant.
 * Route: /tenants/:id/settings/branding
 */
export const TenantBrandingPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenantTenant();
  const { t } = useI18n();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  // Logo & Favicon
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');

  // Colors
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#1E40AF');

  // Custom Domain
  const [customDomain, setCustomDomain] = useState('');
  const [domainStatus, setDomainStatus] = useState<'not_configured' | 'pending' | 'verified'>('not_configured');

  // Support & Legal
  const [supportEmail, setSupportEmail] = useState('');
  const [supportUrl, setSupportUrl] = useState('');
  const [termsUrl, setTermsUrl] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');

  // Custom CSS (key-value map)
  const [cssEntries, setCssEntries] = useState<CssEntry[]>([]);

  // Load tenant on mount
  useEffect(() => {
    if (id) {
      store.GetTenant(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  // Initialize form from store
  useEffect(() => {
    if (store.currentItem) {
      const item = store.currentItem as any;
      const branding = item.branding ?? {};

      setLogoUrl(branding.logo_url ?? '');
      setFaviconUrl(branding.favicon_url ?? '');
      setPrimaryColor(branding.primary_color ?? '#3B82F6');
      setSecondaryColor(branding.secondary_color ?? '#1E40AF');
      setCustomDomain(branding.custom_domain ?? '');
      setSupportEmail(branding.support_email ?? '');
      setSupportUrl(branding.support_url ?? '');
      setTermsUrl(branding.terms_url ?? '');
      setPrivacyUrl(branding.privacy_url ?? '');

      // Determine domain status
      if (branding.custom_domain) {
        setDomainStatus(branding.domain_verified ? 'verified' : 'pending');
      } else {
        setDomainStatus('not_configured');
      }

      // Parse custom_css map into entries
      const cssMap = branding.custom_css ?? {};
      const entries: CssEntry[] = Object.entries(cssMap).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      setCssEntries(entries.length > 0 ? entries : []);
    }
  }, [store.currentItem]);

  const tenantName = (store.currentItem as any)?.name || t('tenant.title');

  // ---------------------------------------------------------------------------
  // CSS entries management
  // ---------------------------------------------------------------------------

  const handleAddCssEntry = () => {
    setCssEntries((prev) => [...prev, { key: '', value: '' }]);
  };

  const handleRemoveCssEntry = (index: number) => {
    setCssEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCssEntryChange = (index: number, field: 'key' | 'value', val: string) => {
    setCssEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry))
    );
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const handleSaveAll = async () => {
    if (!id) return;
    setSaveError(null);
    setSaved(false);
    setIsSubmitting(true);

    // Build custom_css map from entries
    const customCssMap: Record<string, string> = {};
    cssEntries.forEach((entry) => {
      const trimmedKey = entry.key.trim();
      if (trimmedKey) {
        customCssMap[trimmedKey] = entry.value;
      }
    });

    const brandingPayload = {
      logo_url: logoUrl,
      favicon_url: faviconUrl,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      custom_domain: customDomain,
      support_email: supportEmail,
      support_url: supportUrl,
      terms_url: termsUrl,
      privacy_url: privacyUrl,
      custom_css: customCssMap,
    };

    try {
      await store.UpdateTenant(id, {
        tenant: { ...(store.currentItem as any), branding: brandingPayload },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

      // Update domain status based on new value
      if (customDomain) {
        setDomainStatus((prev) => (prev === 'verified' ? 'verified' : 'pending'));
      } else {
        setDomainStatus('not_configured');
      }
    } catch (err) {
      setSaveError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Domain format validation helper
  // ---------------------------------------------------------------------------

  const isDomainValid = (domain: string): boolean => {
    if (!domain) return true;
    return /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(domain);
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (store.isLoading && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb & header */}
      <div>
        <nav className="text-sm">
          <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
            {t('tenant.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
            {tenantName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-muted-foreground">{t('tenant.settings')}</span>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('tenant.branding')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('tenant.branding')}</h1>
      </div>

      {/* Global alerts */}
      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('tenant.saveSuccess')}
        </Alert>
      )}

      {saveError ? (
        <ErrorAlert error={saveError} dismissible onDismiss={() => setSaveError(null)} />
      ) : null}

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* ================================================================= */}
      {/* Card 1: Logo & Favicon */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.logoFavicon')}</CardTitle>
          <CardDescription>{t('tenant.logoFaviconDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              {t('tenant.logo')}
            </label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/50 flex-shrink-0 overflow-hidden">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={t('tenant.logoPreview')}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : (
                  <svg
                    className="w-8 h-8 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21z"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  label={t('tenant.logoUrl')}
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                  <svg
                    className="w-6 h-6 text-muted-foreground mx-auto mb-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <p className="text-xs text-muted-foreground">{t('tenant.uploadPlaceholder')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Favicon */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              {t('tenant.favicon')}
            </label>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md border-2 border-dashed border-border flex items-center justify-center bg-muted/50 flex-shrink-0 overflow-hidden">
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt={t('tenant.faviconPreview')}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <svg
                    className="w-5 h-5 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <Input
                  label={t('tenant.faviconUrl')}
                  type="url"
                  value={faviconUrl}
                  onChange={(e) => setFaviconUrl(e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Card 2: Colors */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.colors')}</CardTitle>
          <CardDescription>{t('tenant.colorsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Primary Color */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('tenant.primaryColor')}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 rounded border border-input cursor-pointer p-0.5"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#3B82F6"
                  className="flex-1"
                />
              </div>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('tenant.secondaryColor')}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-12 h-10 rounded border border-input cursor-pointer p-0.5"
                />
                <Input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#1E40AF"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('tenant.colorPreview')}
            </label>
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div
                className="rounded-lg p-4"
                style={{ backgroundColor: primaryColor }}
              >
                <h3 className="text-sm font-semibold text-white">{t('tenant.previewCardTitle')}</h3>
                <p className="text-xs text-white/80 mt-1">{t('tenant.previewCardDescription')}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('tenant.previewPrimaryButton')}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors"
                  style={{ backgroundColor: secondaryColor }}
                >
                  {t('tenant.previewSecondaryButton')}
                </button>
                <span
                  className="text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  {t('tenant.previewLink')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: primaryColor }}
                />
                <span className="text-xs text-muted-foreground font-mono">{primaryColor}</span>
                <div
                  className="w-4 h-4 rounded-full ml-4"
                  style={{ backgroundColor: secondaryColor }}
                />
                <span className="text-xs text-muted-foreground font-mono">{secondaryColor}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Card 3: Custom Domain */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.customDomain')}</CardTitle>
          <CardDescription>{t('tenant.customDomainDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Input
              label={t('tenant.domain')}
              type="text"
              value={customDomain}
              onChange={(e) => {
                setCustomDomain(e.target.value);
                if (e.target.value && e.target.value !== (store.currentItem as any)?.branding?.custom_domain) {
                  setDomainStatus('pending');
                } else if (!e.target.value) {
                  setDomainStatus('not_configured');
                }
              }}
              placeholder="app.yourcompany.com"
            />
            {customDomain && !isDomainValid(customDomain) && (
              <p className="text-xs text-red-500 mt-1">
                {t('tenant.domainFormatError')}
              </p>
            )}
          </div>

          {/* Domain status indicator */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
            <span className="text-sm font-medium text-muted-foreground">{t('tenant.domainStatus')}:</span>
            {domainStatus === 'verified' && (
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <Badge variant="success" size="sm">{t('tenant.domainVerified')}</Badge>
              </div>
            )}
            {domainStatus === 'pending' && (
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <Badge variant="warning" size="sm">{t('tenant.domainPending')}</Badge>
              </div>
            )}
            {domainStatus === 'not_configured' && (
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                <Badge variant="secondary" size="sm">{t('tenant.domainNotConfigured')}</Badge>
              </div>
            )}
          </div>

          {customDomain && domainStatus === 'pending' && (
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                {t('tenant.domainPendingHint')}
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 font-mono">
                CNAME {customDomain} → app.corde.io
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Card 4: Support & Legal */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.supportLegal')}</CardTitle>
          <CardDescription>{t('tenant.supportLegalDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label={t('tenant.supportEmail')}
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            placeholder="support@yourcompany.com"
          />
          <div>
            <Input
              label={t('tenant.supportUrl')}
              type="url"
              value={supportUrl}
              onChange={(e) => setSupportUrl(e.target.value)}
              placeholder="https://support.yourcompany.com"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.supportUrlHint')}
            </p>
          </div>
          <div>
            <Input
              label={t('tenant.termsUrl')}
              type="url"
              value={termsUrl}
              onChange={(e) => setTermsUrl(e.target.value)}
              placeholder="https://yourcompany.com/terms"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.termsUrlHint')}
            </p>
          </div>
          <div>
            <Input
              label={t('tenant.privacyUrl')}
              type="url"
              value={privacyUrl}
              onChange={(e) => setPrivacyUrl(e.target.value)}
              placeholder="https://yourcompany.com/privacy"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.privacyUrlHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Card 5: Custom CSS */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.customCss')}</CardTitle>
          <CardDescription>{t('tenant.customCssDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cssEntries.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">{t('tenant.noCssEntries')}</p>
            </div>
          )}

          {cssEntries.map((entry, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="flex-1">
                <Input
                  label={index === 0 ? t('tenant.cssKey') : undefined}
                  type="text"
                  value={entry.key}
                  onChange={(e) => handleCssEntryChange(index, 'key', e.target.value)}
                  placeholder="--brand-color"
                />
              </div>
              <div className="flex-1">
                <Input
                  label={index === 0 ? t('tenant.cssValue') : undefined}
                  type="text"
                  value={entry.value}
                  onChange={(e) => handleCssEntryChange(index, 'value', e.target.value)}
                  placeholder="#3B82F6"
                />
              </div>
              <div className={index === 0 ? 'mt-7' : ''}>
                <button
                  type="button"
                  onClick={() => handleRemoveCssEntry(index)}
                  className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                  aria-label={t('tenant.removeCssEntry')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={handleAddCssEntry}
            className="w-full"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            {t('tenant.addCssEntry')}
          </Button>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Save All Footer */}
      {/* ================================================================= */}
      <div className="flex items-center justify-between pt-2 pb-6">
        <Link to={`/tenants/${id}/settings`}>
          <Button type="button" variant="secondary">{t('common.cancel')}</Button>
        </Link>
        <Button
          onClick={handleSaveAll}
          disabled={isSubmitting}
          className="min-w-[140px]"
        >
          {isSubmitting ? t('common.saving') : t('tenant.saveAllBranding')}
        </Button>
      </div>
    </div>
  );
});

export default TenantBrandingPage;
